import apiClient, { getApiErrorMessage } from './apiClient';

const unwrap = (data) => data?.data || data?.Data || data;

const unwrapList = (data) => {
  const value = unwrap(data);
  if (Array.isArray(value)) {
    return value;
  }
  if (Array.isArray(value?.items)) {
    return value.items;
  }
  return [];
};

const normalizePackage = (item) => ({
  id: item?.id || item?.Id,
  slug: item?.slug || item?.Slug,
  kind: item?.kind || item?.Kind,
  basePackageId: item?.basePackageId || item?.BasePackageId || null,
  nameFa: item?.nameFa || item?.NameFa || '',
  nameEn: item?.nameEn || item?.NameEn || '',
  priceToman: item?.priceToman ?? item?.PriceToman ?? 0,
  payableToman: item?.payableToman ?? item?.PayableToman ?? 0,
  discountPercent: item?.discountPercent ?? item?.DiscountPercent ?? 0,
  durationDays: item?.durationDays ?? item?.DurationDays ?? 30,
  maxMembers: item?.maxMembers ?? item?.MaxMembers ?? null,
  messagesPer3Hours: item?.messagesPer3Hours ?? item?.MessagesPer3Hours ?? 0,
  filesPerDay: item?.filesPerDay ?? item?.FilesPerDay ?? 0,
  canCreateProduct: item?.canCreateProduct ?? item?.CanCreateProduct ?? false,
  maxActivePublishes: item?.maxActivePublishes ?? item?.MaxActivePublishes ?? 0,
  deepResearchLevel: item?.deepResearchLevel || item?.DeepResearchLevel || 'None',
  canVoice: item?.canVoice ?? item?.CanVoice ?? false,
  canArtifacts: item?.canArtifacts ?? item?.CanArtifacts ?? false,
  hideAds: item?.hideAds ?? item?.HideAds ?? false,
  canCustomGpt: item?.canCustomGpt ?? item?.CanCustomGpt ?? false,
  canGamma: item?.canGamma ?? item?.CanGamma ?? false,
  canSuno: item?.canSuno ?? item?.CanSuno ?? false,
  models: unwrapList(item?.models || item?.Models)
    .map((model) => ({
      modelId: model?.modelId || model?.ModelId || '',
      tokensPer3Hours: model?.tokensPer3Hours ?? model?.TokensPer3Hours ?? 0,
      weeklyUnitQuota: model?.weeklyUnitQuota ?? model?.WeeklyUnitQuota ?? null
    }))
    .filter((model) => model.modelId)
});

export const fetchPackagesApi = async () => {
  const { data } = await apiClient.get('/packages');
  return unwrapList(data).map(normalizePackage);
};

export const fetchMyPackageApi = async () => {
  const { data } = await apiClient.get('/packages/me');
  return normalizeEntitlement(unwrap(data));
};

const normalizeEntitlement = (item) => ({
  hasPackage: Boolean(item?.hasPackage ?? item?.HasPackage),
  packageNameFa: item?.packageNameFa || item?.PackageNameFa || '',
  packageNameEn: item?.packageNameEn || item?.PackageNameEn || '',
  packageSlug: item?.packageSlug || item?.PackageSlug || null,
  purchasedPackageSlug: item?.purchasedPackageSlug || item?.PurchasedPackageSlug || item?.packageSlug || item?.PackageSlug || null,
  packageKind: item?.packageKind || item?.PackageKind || null,
  source: item?.source || item?.Source || null,
  startsAt: item?.startsAt || item?.StartsAt || null,
  expiresAt: item?.expiresAt || item?.ExpiresAt || null,
  remainingDays: item?.remainingDays ?? item?.RemainingDays ?? null,
  remainingCreditToman: item?.remainingCreditToman ?? item?.RemainingCreditToman ?? null,
  durationDays: item?.durationDays ?? item?.DurationDays ?? null,
  priceToman: item?.priceToman ?? item?.PriceToman ?? null,
  canCreateProduct: Boolean(item?.canCreateProduct ?? item?.CanCreateProduct),
  maxActivePublishes: item?.maxActivePublishes ?? item?.MaxActivePublishes ?? 0,
  activePublishes: item?.activePublishes ?? item?.ActivePublishes ?? 0,
  publishSlotsRemaining: item?.publishSlotsRemaining ?? item?.PublishSlotsRemaining ?? 0,
  upgradeNotice: item?.upgradeNotice || item?.UpgradeNotice || '',
  freeTierModelId: item?.freeTierModelId || item?.FreeTierModelId || null,
  freeTierTokenLimit: item?.freeTierTokenLimit ?? item?.FreeTierTokenLimit ?? null,
  freeTierTokensRemaining: item?.freeTierTokensRemaining ?? item?.FreeTierTokensRemaining ?? null,
  quotaWindowHours: item?.quotaWindowHours ?? item?.QuotaWindowHours ?? 3,
  freeQuotaResetsAt: item?.freeQuotaResetsAt || item?.FreeQuotaResetsAt || null,
  messagesRemaining: item?.messagesRemaining ?? item?.MessagesRemaining ?? 0
});

export const purchasePackageApi = async ({ packageId, payFromWallet, voucherCode, provider, organizationName }) => {
  try {
    const { data } = await apiClient.post(`/packages/${encodeURIComponent(packageId)}/purchase`, {
      payFromWallet,
      voucherCode,
      provider,
      organizationName
    });
    return unwrap(data);
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Package purchase failed.'));
  }
};

export const fetchOrganizationMembersApi = async () => {
  const { data } = await apiClient.get('/packages/organization/members');
  return unwrapList(data);
};

export const addOrganizationMemberApi = async (phoneNumber) => {
  try {
    const { data } = await apiClient.post('/packages/organization/members', { phoneNumber });
    return unwrapList(data);
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Adding organization member failed.'));
  }
};

export const removeOrganizationMemberApi = async (userId) => {
  await apiClient.delete(`/packages/organization/members/${encodeURIComponent(userId)}`);
};
